import uniq from "lodash/uniq";
import React, { useEffect, useRef, useState } from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";
import {
  Dropdown,
  Icon,
  IconGroup,
  Popup,
  SemanticICONS,
} from "semantic-ui-react";
import styled from "styled-components";
import * as uuid from "uuid";
import {
  FetchStreamsAPIResponse,
  Filters,
  SearchDevicesFiltersType,
  fetchDeviceFilterOptions,
} from "../../../../../BytebeamClient";
import {
  ClickhouseToTSTypes,
  User,
  getTypeFromClickhouseToTS,
  isInputValueColumnTypeValid,
} from "../../../../../util";
import { beamtoast } from "../../../../common/CustomToast";
import { objectToQuery } from "../../../common/QuerySelector";
import { capitalizeFirstLetter } from "../../../util";
import { StyledButtonWithBorder } from "../Devices";
import EditableFilterRow, { HeartbeatFilterOptions } from "./EditableFilterRow";

export type DeviceFiltersOptionsType = {
  key: string;
  text: string;
  icon: SemanticICONS;
  value: `${"state" | "metadata"}:${string}`;
  passingvalue: string;
};

export interface FilterState {
  id: string;
  field: string;
  columnType: ClickhouseToTSTypes;
  type: "metadata" | "state";
  operator: string;
  isNullOperator: boolean;
  value: string | string[] | null;
  customTimeValue?: number;
  customTimeUnit?: string;
}

export const StyledDropdown = styled(Dropdown)`
  & .item {
    .label {
      min-width: 4rem;
    }
    .text {
      word-break: break-word;
    }
  }
`;

const FilterButtonContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const FilterBubble = styled.div`
  position: absolute;
  top: -8px;
  right: -8px;
  background-color: red;
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 700;
`;

export const FilterRow = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  width: 100%;
`;

const FilterActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 10px;
  padding: 16px 0px 0px 0px;
  border-top: 1px solid
    ${({ theme }) => theme.colors["container-secondary-border-color"]};
`;

interface FilterPanelProps {
  $position?: "left" | "right";
}

const FilterPanel = styled.div<FilterPanelProps>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: absolute;
  top: 100%;
  ${({ $position }) => ($position === "left" ? "left: 0;" : "right: 0;")}
  width: 636px;
  background: ${({ theme }) => theme.colors["container-background"]};
  border: 1px solid
    ${({ theme }) => theme.colors["container-secondary-border-color"]};
  border-radius: 12px;
  box-shadow: 4px 4px 4px 1px rgba(0, 0, 0, 0.1);
  z-index: 100;
  margin-top: 12px;
  padding: 16px;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: ${({ theme }) => theme.colors["background-color-2"]};
`;

export const HeartbeatField = "last_heartbeat";

const convertCustomTimeToSeconds = (value: number, unit: string): number => {
  const conversions = {
    seconds: 1,
    minutes: 60,
    hours: 60 * 60,
    days: 24 * 60 * 60,
    weeks: 7 * 24 * 60 * 60,
    months: 30 * 24 * 60 * 60,
    years: 365 * 24 * 60 * 60,
  };

  return value * conversions[unit];
};

export const stateFiltersToQuery = (
  filters: FilterState[]
): SearchDevicesFiltersType["stateFilters"] => {
  let stateFilters: SearchDevicesFiltersType["stateFilters"] = [];

  const regularStateFilters: FilterState[] = filters.filter(
    (f) =>
      f.type === "state" && f.field && f.value && f.field !== HeartbeatField
  );

  // Getting heartbeat filters separately
  const heartbeatFilters: FilterState[] = filters.filter(
    (f) =>
      f.type === "state" &&
      f.field === HeartbeatField &&
      (f.value || (f.customTimeValue && f.customTimeUnit))
  );

  // both regular and heartbeat filters
  const allStateFilters: any[] = [...regularStateFilters];

  // Convert heartbeat filters to insert_timestamp filters
  heartbeatFilters.forEach((heartbeatFilter) => {
    let timeValueInSeconds: number;

    // Handling custom time input
    if (
      heartbeatFilter.value === "custom" &&
      heartbeatFilter.customTimeValue &&
      heartbeatFilter.customTimeUnit
    ) {
      timeValueInSeconds = convertCustomTimeToSeconds(
        heartbeatFilter.customTimeValue,
        heartbeatFilter.customTimeUnit
      );
    } else {
      // Handling predefined options
      const option = HeartbeatFilterOptions.find(
        (opt) => opt.key === heartbeatFilter.value
      );
      if (!option) return; // Skip if option not found
      timeValueInSeconds = option.value ?? 0; // Default to 0 if option.value is null
    }

    const currentTimeInSeconds = Math.floor(Date.now() / 1000);
    const timestampValue = currentTimeInSeconds - timeValueInSeconds;

    // Add the converted heartbeat filter as an insert_timestamp filter
    allStateFilters.push({
      id: heartbeatFilter.id,
      field: "insert_timestamp",
      columnType: "int",
      type: "state",
      operator: ">",
      isNullOperator: false,
      value: String(timestampValue),
    });
  });

  if (allStateFilters.length > 0) {
    // Group state filters by field, operator and value.
    const stateQuery = allStateFilters.map((f) => ({
      column: f.field,
      operator: f.operator,
      isNullOperator: f.isNullOperator,
      columnType: f.columnType,
      value: String(f.value),
    }));
    // Converting object to query which is expected
    stateFilters = objectToQuery(stateQuery);
  }

  return stateFilters;
};

const buildSearchQuery = (filters: FilterState[]): SearchDevicesFiltersType => {
  const stateFilters = filters.filter((f) => f.type === "state");
  const metadataFilters: FilterState[] = filters.flatMap((f) => {
    if (f.type === "metadata" && f.field && f.value) {
      if (f.isNullOperator) {
        return [
          {
            ...f,
            value: f.operator === "=" ? null : "", // sending null for IS NULL and empty string for IS NOT NULL
          },
        ];
      }
      return [f];
    }
    return [];
  });

  const query: SearchDevicesFiltersType = {};

  if (stateFilters.length > 0) {
    query.stateFilters = stateFiltersToQuery(filters);
  }

  if (metadataFilters.length > 0) {
    // Convert arrays with only one item to a single value, except for the "id" key, which should always be an array.
    query.metaDatafilters = metadataFilters.reduce((acc, f) => {
      if (f.isNullOperator) {
        if (f.operator === "=") {
          // @ts-ignore
          acc[f.field] = null;
        } else {
          // @ts-ignore
          acc[f.field] = "";
        }
      } else {
        const value = f?.value ?? [];
        if (Array.isArray(value)) {
          acc[f.field] = value.map((val) => String(val));
        } else {
          acc[f.field] = [String(value)];
        }
      }

      return acc;
    }, {} as Filters);
  }

  return query;
};

/**
 * Get the fields options for the dropdown based on the metadata keys and state keys, and the serial key.
 * Label is the full label when searching in dropdown, else show the one letter label.
 * @param serialMetadataKey - The serial key of the metadata.
 * @param stateKeys - The state keys of the device.
 * @param metadataKeys - The metadata keys of the device.
 */
const getFieldsOptions = (
  serialMetadataKey: string | null | undefined,
  stateKeys: string[],
  metadataKeys: string[]
): DeviceFiltersOptionsType[] => {
  /**
   * Creates a dropdown option for device filters.
   *
   * @param key - The key for the dropdown option.
   * @param type - The type of the filter, either "state" or "metadata".
   * @returns An object representing the dropdown option for device filters.
   */
  const createDropdownOption = (
    key: string,
    type: "state" | "metadata"
  ): DeviceFiltersOptionsType => {
    return {
      key: uuid.v4(),
      icon: type === "state" ? "barcode" : "tag",
      text: capitalizeFirstLetter(key),
      value: `${type}:${key}`,
      passingvalue: key,
    };
  };

  const deviceShadowOptions = stateKeys
    .filter((key) => key !== "timestamp" && key !== "insert_timestamp")
    .map((key) => createDropdownOption(key, "state"));

  const keyOptions = [
    ...metadataKeys,
    ...(serialMetadataKey && !metadataKeys.includes(serialMetadataKey)
      ? [serialMetadataKey]
      : []),
  ].map((key) => createDropdownOption(key, "metadata"));

  const heartbeatOption: DeviceFiltersOptionsType = {
    key: HeartbeatField,
    text: "Last Heartbeat",
    value: `state:${HeartbeatField}`,
    passingvalue: HeartbeatField,
    icon: "barcode",
  };

  const options = uniq([
    {
      key: "id",
      text: "Device ID",
      value: "metadata:id",
      passingvalue: "id",
      icon: "barcode",
    },
    keyOptions.length > 0 && {
      key: "header1",
      text: "Metadata Options",
      value: "header1",
      icon: "tag",
      disabled: true,
    },
    ...keyOptions,
    deviceShadowOptions.length > 0 && {
      key: "header2",
      text: "Device Shadow Options",
      value: "header2",
      icon: "barcode",
      disabled: true,
    },
    ...deviceShadowOptions,
    // Added heartbeat option
    heartbeatOption,
  ]).filter(Boolean);

  return options;
};

type FilterProps = {
  readonly user: User;
  readonly stateKeys: any;
  readonly metadataKeys: any;
  readonly detailedStreamsList: FetchStreamsAPIResponse;
  readonly onSearch: (filter: any) => void;
  devicesLoading: boolean;
  showRefreshButton: boolean;
  refreshDevices: () => void;
  /**
   * Position of the filter panel relative to the filter button.
   * @default "right"
   */
  panelPosition?: "left" | "right";
} & RouteComponentProps;

const DevicesFilterUI: React.FC<FilterProps> = ({
  user,
  stateKeys,
  metadataKeys,
  detailedStreamsList,
  onSearch,
  devicesLoading,
  showRefreshButton,
  refreshDevices,
  history,
  location,
  panelPosition = "right",
}) => {
  const serialMetadataKey = user?.["tenant-settings"]?.["serial-key"];
  const filterPanelRef = useRef<HTMLDivElement>(null);
  const filterButtonRef = useRef<HTMLDivElement>(null);

  const [fieldsOptions, setFieldsOptions] = useState<
    DeviceFiltersOptionsType[]
  >([]);

  const defaultFilter = {
    id: uuid.v4(),
    field: "",
    columnType: "int",
    type: "metadata",
    operator: "=",
    isNullOperator: false,
    value: "",
  } as FilterState;

  const [filters, setFilters] = useState<FilterState[]>([defaultFilter]);

  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [filterOptions, setFilterOptions] = useState<{
    [key: string]: string[];
  }>({});

  const updateUrlWithFilters = (filtersToUpdate: FilterState[]): void => {
    const searchParams = new URLSearchParams(location.search);

    if (filtersToUpdate.length > 0) {
      const filtersStr = JSON.stringify(filtersToUpdate);
      searchParams.set("filters", filtersStr);
    } else {
      searchParams.delete("filters");
    }

    history.replace({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
  };

  const loadFiltersFromUrl = (): FilterState[] => {
    const searchParams = new URLSearchParams(location.search);
    const filtersStr = searchParams.get("filters");

    if (filtersStr) {
      try {
        const parsed = JSON.parse(filtersStr);
        // This ensures MultiSelectDropdown's includes() check works correctly
        return parsed.map((filter: FilterState) => {
          if (filter.field === "id" && Array.isArray(filter.value)) {
            return {
              ...filter,
              value: filter.value.map((v) => String(v)),
            };
          }
          return filter;
        });
      } catch (error) {
        console.error("Error parsing filters from URL:", error);
        return [];
      }
    }

    return []; // fall back to empty array if no filters are found in url
  };

  // Load filters from URL when component mounts
  useEffect(() => {
    const filtersFromUrl = loadFiltersFromUrl();

    if (filtersFromUrl && filtersFromUrl.length > 0) {
      setFilters(filtersFromUrl);
      onSearch(buildSearchQuery(filtersFromUrl));
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps
  // only runs when URL query string changes

  /**
   * Retrieves the TypeScript type corresponding to a given device shadow field.
   *
   * @param {string} field - The name of the device shadow field.
   * @returns {string} - The TypeScript type of the specified field.
   */
  const getDeviceShadowFieldType = (field: string): ClickhouseToTSTypes => {
    if (field === "id") {
      return "int";
    }
    const columnMeta = detailedStreamsList["device_shadow"]?.fields?.[field];
    const columnType = columnMeta?.type || "string";
    return getTypeFromClickhouseToTS(columnType);
  };

  const getDeviceShadowFieldTypeNullable = (field: string): boolean => {
    const columnMeta = detailedStreamsList["device_shadow"]?.fields?.[field];
    const columnType = columnMeta?.type || "string";
    return columnType?.toLowerCase().includes("null");
  };

  /**
   * Adds a filter to the list of filters and triggers a search with the updated filters.
   * Resets the UI, current filter to a new filter.
   */
  const addFilter = () => {
    const lastFilter = filters[filters.length - 1];

    if (lastFilter.field === HeartbeatField && lastFilter.value === "custom") {
      if (lastFilter.customTimeValue === undefined) {
        beamtoast.error(`Custom Time value should not be empty`);
        return;
      }
      if (lastFilter.customTimeValue <= 0) {
        beamtoast.error(
          `Custom Time value should be greater than 0 ${lastFilter.customTimeUnit ?? "minutes"}`
        );
        return;
      }
    }

    let isValidInput = true;
    if (lastFilter.type === "state") {
      isValidInput = isInputValueColumnTypeValid(
        lastFilter.value as string,
        lastFilter.columnType
      );
    }

    if (
      lastFilter.field === "id" ||
      lastFilter.type === "metadata" ||
      isValidInput
    ) {
      const updatedFilters = [
        ...filters.slice(0, -1),
        lastFilter,
        { ...defaultFilter, id: uuid.v4() },
      ];

      setFilters(updatedFilters);
    }
  };

  const removeFilter = (filterId: string) => {
    const newFilters = filters.filter((filter) => filter.id !== filterId);

    // If nothing left, reset to one default filter
    if (newFilters.length === 0) {
      setFilters([{ ...defaultFilter, id: uuid.v4() }]);
      updateUrlWithFilters([]);
      onSearch("");
    } else {
      setFilters(newFilters);
      updateUrlWithFilters(newFilters);
      onSearch(buildSearchQuery(newFilters));
    }
  };

  const resetFilters = () => {
    setFilters([{ ...defaultFilter, id: uuid.v4() }]);
    // Clear URL parameters when resetting filters
    updateUrlWithFilters([]);
    onSearch("");
  };

  // Get the fields options for the dropdown based on the metadata keys and state keys, and the serial key.
  useEffect(() => {
    setFieldsOptions(
      getFieldsOptions(serialMetadataKey, stateKeys, metadataKeys)
    );
  }, [stateKeys, metadataKeys, serialMetadataKey]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showFilterPanel &&
        filterPanelRef.current &&
        !filterPanelRef.current.contains(event.target as Node) &&
        filterButtonRef.current &&
        !filterButtonRef.current.contains(event.target as Node)
      ) {
        setShowFilterPanel(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showFilterPanel]);

  useEffect(() => {
    // Add function to fetch filter options
    const fetchFilterOptions = async () => {
      try {
        const options = await fetchDeviceFilterOptions({});
        const optionsMap = options.reduce(
          (acc, curr) => {
            acc[curr.filterName] = curr.filterValues;
            return acc;
          },
          {} as { [key: string]: string[] }
        );
        setFilterOptions(optionsMap);
      } catch (error) {
        console.error("Error fetching filter options:", error);
      }
    };

    fetchFilterOptions();
  }, [devicesLoading]);

  // Add a function to update an existing filter
  const updateFilter = (
    id: string,
    updatedFilter: FilterState,
    refresh: boolean = false
  ) => {
    const newFilters = filters.map((filter) =>
      filter.id === id ? updatedFilter : filter
    );
    setFilters(newFilters);

    if (refresh) {
      // Update URL with updated filters
      updateUrlWithFilters(newFilters);

      // Rebuild search query with updated filters
      onSearch(buildSearchQuery(newFilters));
    }
  };

  const validFiltersCount =
    filters?.filter((filter) => filter?.field && filter?.value)?.length ?? 0;

  return (
    <div
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: "12px",
      }}
    >
      {showRefreshButton && (
        <Popup
          content={"Refresh filtered devices to see the latest data"}
          position="top center"
          inverted
          trigger={
            <div>
              <FilterButtonContainer>
                <StyledButtonWithBorder
                  style={{
                    padding: "10.5px",
                  }}
                  onClick={refreshDevices}
                  disabled={devicesLoading}
                >
                  <Icon
                    name="refresh"
                    style={{ margin: 0 }}
                    loading={devicesLoading}
                  />
                </StyledButtonWithBorder>
                <FilterBubble>!</FilterBubble>
              </FilterButtonContainer>
            </div>
          }
        />
      )}

      {validFiltersCount > 0 && (
        <Popup
          content={"Reset filters to see all devices"}
          position="top center"
          inverted
          trigger={
            <div>
              <FilterButtonContainer>
                <StyledButtonWithBorder
                  onClick={resetFilters}
                  style={{
                    padding: "10.5px",
                  }}
                >
                  <IconGroup style={{ margin: 0 }}>
                    <Icon name="filter" />
                    <Icon corner name="cancel" style={{ textShadow: "none" }} />
                  </IconGroup>
                </StyledButtonWithBorder>
              </FilterButtonContainer>
            </div>
          }
        />
      )}

      <FilterButtonContainer ref={filterButtonRef}>
        <StyledButtonWithBorder
          onClick={() => setShowFilterPanel(!showFilterPanel)}
          style={{
            padding: "10.5px",
          }}
        >
          <Icon name="filter" style={{ margin: 0 }} />
        </StyledButtonWithBorder>
        {validFiltersCount > 0 && (
          <FilterBubble>{validFiltersCount}</FilterBubble>
        )}
      </FilterButtonContainer>

      {showFilterPanel && (
        <FilterPanel ref={filterPanelRef} $position={panelPosition}>
          {filters.length > 0 && (
            <FilterGroup>
              {filters.map((filter) => {
                const usedFields = filters
                  .filter(
                    (f) =>
                      f.id !== filter.id && f.field && f.type === "metadata"
                  )
                  .map((f) => `${f.type}:${f.field}`);

                let filteredFieldsOptions = fieldsOptions.filter(
                  (option) => !usedFields.includes(option.value)
                );

                const currentFieldValue = `${filter.type}:${filter.field}`;
                const currentFieldOption = fieldsOptions.find(
                  (option) => option.value === currentFieldValue
                );

                if (
                  currentFieldOption &&
                  !filteredFieldsOptions.some(
                    (option) => option.value === currentFieldValue
                  )
                ) {
                  filteredFieldsOptions = [
                    currentFieldOption,
                    ...filteredFieldsOptions,
                  ];
                }

                return (
                  <EditableFilterRow
                    key={filter.id}
                    filters={filters}
                    filter={filter}
                    fieldsOptions={filteredFieldsOptions}
                    updateFilter={updateFilter}
                    removeFilter={removeFilter}
                    getDeviceShadowFieldType={getDeviceShadowFieldType}
                    getDeviceShadowFieldTypeNullable={
                      getDeviceShadowFieldTypeNullable
                    }
                    filterOptions={filterOptions}
                  />
                );
              })}
            </FilterGroup>
          )}
          <FilterActions>
            <StyledButtonWithBorder
              onClick={() => {
                addFilter();
              }}
              style={{
                padding: "8px 12px",
                fontSize: "11px",
              }}
            >
              <Icon name="plus" style={{ fontSize: "13px" }} />
              Add
            </StyledButtonWithBorder>
            <div style={{ display: "flex", gap: "16px" }}>
              <StyledButtonWithBorder
                onClick={resetFilters}
                style={{
                  padding: "8px 12px",
                  fontSize: "11px",
                }}
              >
                <IconGroup style={{ fontSize: "13px" }}>
                  <Icon name="filter" />
                  <Icon corner name="cancel" style={{ textShadow: "none" }} />
                </IconGroup>
                Reset
              </StyledButtonWithBorder>
            </div>
          </FilterActions>
        </FilterPanel>
      )}
    </div>
  );
};

export default withRouter(React.memo(DevicesFilterUI));
